// src/components/ToDoListItem.jsx
import {
  ListItemButton,
  ListItemIcon,
  IconButton,
  Checkbox,
  Typography,
  Box,
  Chip,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Stack,
  CircularProgress,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ImageList,
  ImageListItem,
  FormHelperText,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Check,
  Close,
  MoreVert,
  Folder,
  Inbox,
} from "@mui/icons-material";
import { fullName, sanitizeHtml } from "../../utils/formatting";
import { useCallback, useEffect, useRef, useState } from "react";
import ApiAutoComplete from "../ApiAutoComplete";
import api from "../../utils/api";
import { apiUrls } from "../../utils/urls";
import dayjs from "dayjs";
import useActionMenu from "../../hooks/ui/useActionMenu";
import { DatePicker } from "@mui/x-date-pickers-pro";
import useDisclosure from "../../hooks/ui/useDisclosure";
import { showSuccessSnackbar } from "../../utils/feedback";
import { useProfile } from "../../context/ProfileContext";

const DESCRIPTION_MAX_HEIGHT = 50;
const DESCRIPTION_MAX_HEIGHT_UNIT = "px";

const INFO_CHIP_PROPS = {
  size: "small",
  color: "info",
  sx: {
    height: "16px",
    fontSize: "0.65rem",
    "& .MuiChip-label": { px: 0.5 },
  },
};

function getDelegateeProfileUuid(option) {
  if (!option) {
    return null;
  }
  return option.user_profile?.uuid ?? option.user_profile__uuid ?? null;
}

function getEmployeeOptionLabel(option) {
  if (!option) {
    return "";
  }
  if (option.label) {
    return option.label;
  }
  const user = option.user;
  if (user) {
    const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
    if (name) {
      return name;
    }
  }
  return option.__str__ || "Unnamed";
}

export default function ToDoListItem({
  item,
  completed,
  onToggleComplete,
  questionResult,
  onAnswer,
  loading,
  onItemUpdated,
  divider = true,
  folders = [],
  onMoveToFolder,
  onViewTemplate,
  readOnly = false,
}) {
  const { profile } = useProfile();
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionOverflowing, setDescriptionOverflowing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { template, coverage } = item;
  const {
    title,
    description,
    checklist_items,
    deadline,
    is_question_format,
    images = [],
  } = template;
  const hasChecklist = checklist_items?.length > 0;
  const hasImages = images.length > 0;
  const disabled = Boolean(loading);
  const targetUserProfileId = item.user_profile?.uuid;
  const completedBy = item.completed_by || item.coordination?.completed_by || null;
  const completedByUser = completedBy?.user || null;
  const completedByName = fullName(completedByUser);
  const delegatedByName = fullName(item.delegated_by?.user || null);
  const directDelegateeNames = item.direct_delegatee_names?.trim() || "";
  const completedAt = item.coordination?.completed_at || item.completed_dt || null;
  const completedAtText = completedAt
    ? dayjs(completedAt).format("MMM D, YYYY [at] h:mm A")
    : null;
  const isCoordinatedItem = Boolean(item.coordination?.uuid || template?.is_coordinated);
  const completedByAnotherUser = Boolean(
    isCoordinatedItem &&
      completed &&
      completedBy?.uuid &&
      targetUserProfileId &&
      completedBy?.uuid !== targetUserProfileId
  );
  const interactionDisabled = disabled || readOnly;
  const completionDisabled = interactionDisabled || completedByAnotherUser;
  const completedItemsChanged = useRef(false);
  const descriptionRef = useRef(null);

  const updateDescriptionOverflow = useCallback(() => {
    const el = descriptionRef.current;
    if (!el || !description) {
      setDescriptionOverflowing(false);
      return;
    }
    setDescriptionOverflowing(el.scrollHeight - el.clientHeight > 2);
  }, [description]);

  const [completedChecklistItems, setCompletedChecklistItems] = useState(
    new Set(item.completed_checklist_items)
  );

  const incompletedChecklistItemsCount =
    checklist_items?.length - completedChecklistItems.size;

  const updateChecklistItems = useCallback(async () => {
    try {
      await api.put(apiUrls.scheduling.toDoItem(item.uuid), {
        completed_checklist_items: [...completedChecklistItems],
      });
      if (isCoordinatedItem) {
        onItemUpdated?.();
      }
    } catch (error) {
      console.error("Failed to update checklist items:", error);
    }
  }, [completedChecklistItems, isCoordinatedItem, item.uuid, onItemUpdated]);

  const moveDisclosure = useDisclosure();
  const folderMoveDisclosure = useDisclosure();
  const viewImagesDisclosure = useDisclosure();
  const [selectedDate, setSelectedDate] = useState(dayjs().add(1, "day"));
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [isMoving, setIsMoving] = useState(false);
  const delegateDisclosure = useDisclosure();
  const [isDelegating, setIsDelegating] = useState(false);
  const [selectedDelegatees, setSelectedDelegatees] = useState([]);
  const [delegateError, setDelegateError] = useState("");
  const currentProfileUuid = profile?.uuid;

  const coordinationParticipantIds = new Set(
    (item.coordination_participant_profile_ids || []).map((profileId) =>
      String(profileId)
    )
  );
  const completedItemActionsDisabled = Boolean(completed);

  const handleDelegateSubmit = async () => {
    if (readOnly || completedItemActionsDisabled) {
      return;
    }
    if (!selectedDelegatees.length) {
      setDelegateError("Please select at least one employee to delegate this item.");
      return;
    }

    setIsDelegating(true);
    setDelegateError("");
    try {
      const response = await api.post(apiUrls.scheduling.delegateToDoItem(item.uuid), {
        delegatee_profile_ids: selectedDelegatees
          .map(getDelegateeProfileUuid)
          .filter(Boolean),
      });
      const created = Array.isArray(response.data?.data)
        ? response.data.data
        : response.data?.data
          ? [response.data.data]
          : [];
      const partialErrors = response.data?.errors || [];

      if (created.length) {
        const delegateeCount = created.length;
        showSuccessSnackbar(
          `Delegated to ${delegateeCount} employee${delegateeCount === 1 ? "" : "s"}.`
        );
        delegateDisclosure.onClose();
        setSelectedDelegatees([]);
        onItemUpdated?.();
      }

      if (partialErrors.length) {
        setDelegateError(
          partialErrors.map((entry) => entry.message).filter(Boolean).join(" ")
        );
      }
    } catch (error) {
      const responseError = error?.response?.data;
      if (typeof responseError === "string") {
        setDelegateError(responseError);
      } else if (responseError?.errors?.length) {
        setDelegateError(
          responseError.errors.map((entry) => entry.message).filter(Boolean).join(" ")
        );
      } else if (responseError?.reason) {
        setDelegateError(responseError.reason);
      } else {
        setDelegateError("Unable to delegate this item.");
      }
    } finally {
      setIsDelegating(false);
    }
  };

  const handleMoveToDate = async () => {
    if (readOnly || completedItemActionsDisabled) {
      return;
    }
    if (!selectedDate) return;

    setIsMoving(true);
    try {
      await api.put(apiUrls.scheduling.toDoItem(item.uuid), {
        date: selectedDate.format("YYYY-MM-DD"),
      });

      moveDisclosure.onClose();
      showSuccessSnackbar(`Item moved to ${selectedDate.format("MM/DD/YY")}!`);
      // Call the callback to refresh the todo list
      if (onItemUpdated) {
        onItemUpdated();
      }
    } catch (error) {
      console.error("Failed to move todo item:", error);
    } finally {
      setIsMoving(false);
    }
  };

  const handleMoveToFolder = async () => {
    if (readOnly || completedItemActionsDisabled) {
      return;
    }
    if (!onMoveToFolder) return;

    setIsMoving(true);
    try {
      await onMoveToFolder(item.uuid, selectedFolderId);

      folderMoveDisclosure.onClose();
      const folderName =
        selectedFolderId === null
          ? "Inbox"
          : folders.find((f) => f.uuid === selectedFolderId)?.title || "Folder";
      showSuccessSnackbar(`Item moved to ${folderName}!`);

      // Call the callback to refresh the todo list
      if (onItemUpdated) {
        onItemUpdated();
      }
    } catch (error) {
      console.error("Failed to move todo item to folder:", error);
    } finally {
      setIsMoving(false);
    }
  };

  const { handleOpen, ActionMenu } = useActionMenu({
    actions: [
      {
        label: "View Template",
        onClick: () => {
          onViewTemplate?.(template.uuid, readOnly || !item.created_by_self);
        },
      },
      ...(!readOnly
        ? [
            {
              label: "Move to Folder",
              disabled: completedItemActionsDisabled,
              onClick: () => {
                folderMoveDisclosure.onOpen();
              },
            },
            {
              label: "Delegate Item",
              disabled: completedItemActionsDisabled,
              onClick: () => {
                setDelegateError("");
                setSelectedDelegatees([]);
                delegateDisclosure.onOpen();
              },
            },
            ...(profile?.is_admin
              ? [
                  {
                    label: "Move to a later date",
                    disabled: completedItemActionsDisabled,
                    onClick: () => {
                      moveDisclosure.onOpen();
                    },
                  },
                ]
              : []),
          ]
        : []),
    ],
  });

  useEffect(() => {
    if (!completedItemsChanged.current) {
      return;
    }
    updateChecklistItems();
    completedItemsChanged.current = false;
  }, [completedChecklistItems, updateChecklistItems]);

  useEffect(() => {
    if (completedItemsChanged.current) {
      return;
    }
    setCompletedChecklistItems(new Set(item.completed_checklist_items || []));
  }, [item.uuid, item.completed_checklist_items]);

  useEffect(() => {
    if (!description) {
      setDescriptionExpanded(false);
      setDescriptionOverflowing(false);
      return;
    }

    updateDescriptionOverflow();

    const el = descriptionRef.current;
    if (!el) return;

    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        updateDescriptionOverflow();
      });
      resizeObserver.observe(el);
    }
    window.addEventListener("resize", updateDescriptionOverflow);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateDescriptionOverflow);
    };
  }, [description, descriptionExpanded, updateDescriptionOverflow]);

  let deadlineStr = null;
  if (item.event_trigger) {
    if (template.deadline_event_occurrence_offset != null) {
      deadlineStr = dayjs(item.event_trigger.next_occurrence_date)
        .add(template.deadline_event_occurrence_offset, "day")
        .format("MM/DD/YY");
    } else if (deadline) {
      deadlineStr = dayjs(deadline).format("MM/DD/YY");
    }
  } else if (template.deadline) {
    deadlineStr = dayjs(template.deadline).format("MM/DD/YY");
  }

  return (
    <Box position="relative">
      <ActionMenu />

      <Dialog {...moveDisclosure.getDisclosureProps()} maxWidth="xs" fullWidth>
        <DialogTitle>Move to a later date</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <DatePicker
              label="Select new date"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              disablePast
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "outlined",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={moveDisclosure.onClose} disabled={isMoving}>
            Cancel
          </Button>
          <Button
            onClick={handleMoveToDate}
            variant="contained"
            disabled={
              isMoving || !selectedDate || readOnly || completedItemActionsDisabled
            }
          >
            {isMoving ? "Moving..." : "Move"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        {...folderMoveDisclosure.getDisclosureProps()}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Move to Folder</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <List dense>
              <ListItemButton
                selected={selectedFolderId === null}
                onClick={() => setSelectedFolderId(null)}
              >
                <ListItemIcon>
                  <Inbox />
                </ListItemIcon>
                <ListItemText primary="Inbox" />
              </ListItemButton>
              {folders.map((folder) => (
                <ListItemButton
                  key={folder.uuid}
                  selected={selectedFolderId === folder.uuid}
                  onClick={() => setSelectedFolderId(folder.uuid)}
                >
                  <ListItemIcon>
                    <Folder />
                  </ListItemIcon>
                  <ListItemText primary={folder.title} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={folderMoveDisclosure.onClose} disabled={isMoving}>
            Cancel
          </Button>
          <Button
            onClick={handleMoveToFolder}
            variant="contained"
            disabled={isMoving || readOnly || completedItemActionsDisabled}
          >
            {isMoving ? "Moving..." : "Move"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog {...delegateDisclosure.getDisclosureProps()} maxWidth="sm" fullWidth>
        <DialogTitle>Delegate Item</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <ApiAutoComplete
              multiple
              apiUrl={apiUrls.admin.adminProfiles()}
              label="Employees"
              value={selectedDelegatees}
              onChange={(value) => {
                setSelectedDelegatees(value || []);
                setDelegateError("");
              }}
              defaultParams={{
                search_params: { is_active: true },
                keep_related: true,
                select_related: ["user", "user_profile"],
                size: 50,
              }}
              getOptionLabel={getEmployeeOptionLabel}
              isOptionEqualToValue={(option, value) => {
                const optionProfileUuid = getDelegateeProfileUuid(option);
                const valueProfileUuid = getDelegateeProfileUuid(value);
                return (
                  Boolean(optionProfileUuid && valueProfileUuid) &&
                  String(optionProfileUuid) === String(valueProfileUuid)
                );
              }}
              filterOptions={(options) =>
                options.filter((option) => {
                  const profileUuid = getDelegateeProfileUuid(option);
                  if (!profileUuid) {
                    return false;
                  }
                  if (String(profileUuid) === String(currentProfileUuid)) {
                    return false;
                  }
                  return !coordinationParticipantIds.has(String(profileUuid));
                })
              }
              filterSelectedOptions
              TextFieldProps={{
                error: Boolean(delegateError),
                placeholder: "Search employees",
              }}
            />
            {delegateError && (
              <FormHelperText error sx={{ mt: 1 }}>
                {delegateError}
              </FormHelperText>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              delegateDisclosure.onClose();
              setDelegateError("");
              setSelectedDelegatees([]);
            }}
            disabled={isDelegating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelegateSubmit}
            variant="contained"
            disabled={
              isDelegating ||
              selectedDelegatees.length === 0 ||
              completedItemActionsDisabled
            }
          >
            {isDelegating ? "Delegating..." : "Delegate"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog {...viewImagesDisclosure.getDisclosureProps()} maxWidth="sm" fullWidth>
        <DialogTitle>To-do Images</DialogTitle>
        <DialogContent>
          <ImageList cols={1} gap={12}>
            {images.map((img, index) => (
              <ImageListItem key={img.uuid || index}>
                <img
                  src={img.image}
                  alt={img.title || `To-do image ${index + 1}`}
                  loading="lazy"
                  style={{ borderRadius: 8, cursor: "pointer" }}
                  onClick={() => window.open(img.image, "_blank")}
                />
                {img.title && (
                  <Typography variant="caption" sx={{ mt: 0.5 }}>
                    {img.title}
                  </Typography>
                )}
              </ImageListItem>
            ))}
          </ImageList>
        </DialogContent>
        <DialogActions>
          <Button onClick={viewImagesDisclosure.onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {disabled && (
        <Stack
          justifyContent="center"
          alignItems="center"
          direction="row"
          sx={{
            position: "absolute",
            width: "100%",
            height: "100%",
            top: 0,
            left: 0,
            zIndex: 1,
          }}
        >
          <CircularProgress size={30} />
        </Stack>
      )}

      <ListItemButton
        divider={divider}
        disabled={disabled}
        onClick={hasChecklist ? () => setIsOpen((curr) => !curr) : undefined}
        disableRipple={!hasChecklist}
        disableTouchRipple={!hasChecklist}
        sx={{
          ...(!hasChecklist && {
            "&:hover": { backgroundColor: "transparent" },
            cursor: "default",
          }),
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          {is_question_format ? (
            <>
              <IconButton
                size="small"
                disabled={completionDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onAnswer(true);
                }}
                color={questionResult === true ? "success" : "primary"}
                sx={{
                  backgroundColor:
                    questionResult === true
                      ? "rgba(0,255,0,0.1)"
                      : "transparent",
                }}
              >
                <Check />
              </IconButton>
              <IconButton
                size="small"
                disabled={completionDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  onAnswer(false);
                }}
                color={questionResult === false ? "error" : "primary"}
                sx={{
                  backgroundColor:
                    questionResult === false
                      ? "rgba(255,0,0,0.1)"
                      : "transparent",
                }}
              >
                <Close />
              </IconButton>
            </>
          ) : (
            <Checkbox
              edge="start"
              checked={completed || false}
              onClick={(e) => e.stopPropagation()}
              onChange={onToggleComplete}
              disabled={completionDisabled}
              color={completed ? "success" : "default"}
            />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box>
              <Typography
                variant="body2"
                fontWeight="bold"
                sx={{
                  userSelect: "text",
                  lineHeight: 1.2,
                  mb: 0.25,
                }}
              >
                {title}
              </Typography>
              {(coverage ||
                item.event_trigger ||
                delegatedByName ||
                directDelegateeNames) && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ mb: 0.25 }}
                >
                  {coverage && (
                    <Chip
                      label={`Covering for ${fullName(coverage.coveree.user)}`}
                      {...INFO_CHIP_PROPS}
                    />
                  )}
                  {item.event_trigger && (
                    <Chip
                      label={`${item.event_trigger.title} (${dayjs(
                        item.event_trigger.next_occurrence_date
                      ).format("M/D/YY")})`}
                      {...INFO_CHIP_PROPS}
                    />
                  )}
                  {delegatedByName && (
                    <Chip
                      label={`Delegated by ${delegatedByName}`}
                      {...INFO_CHIP_PROPS}
                    />
                  )}
                  {directDelegateeNames && (
                    <Chip
                      label={`Delegated to ${directDelegateeNames}`}
                      {...INFO_CHIP_PROPS}
                    />
                  )}
                </Stack>
              )}
              {isCoordinatedItem && completedByName && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.25, fontSize: "0.65rem" }}
                >
                  Completed by {completedByName}
                  {completedAtText ? ` on ${completedAtText}` : ""}
                </Typography>
              )}
            </Box>
          }
          disableTypography
          secondary={
            <Box
              sx={{
                fontSize: "0.7rem",
                userSelect: "text",
                lineHeight: 1.3,
                pr: "16px",
                textAlign: "justify",
              }}
            >
              {description && (
                <Box sx={{ mt: 0.25, mb: 0.25 }}>
                  <Box
                    ref={descriptionRef}
                    sx={{
                      overflow: "hidden",
                      maxHeight: descriptionExpanded
                        ? "none"
                        : `${DESCRIPTION_MAX_HEIGHT}${DESCRIPTION_MAX_HEIGHT_UNIT}`,
                      transition: "max-height 0.2s ease",
                      ...(descriptionOverflowing &&
                        !descriptionExpanded && {
                          WebkitMaskImage:
                            "linear-gradient(to bottom, black 72%, transparent 100%)",
                          maskImage:
                            "linear-gradient(to bottom, black 72%, transparent 100%)",
                        }),
                    }}
                  >
                    {sanitizeHtml(description)}
                  </Box>
                </Box>
              )}
              {description && descriptionOverflowing && (
                <Button
                  size="small"
                  variant="text"
                  endIcon={
                    descriptionExpanded ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setDescriptionExpanded((curr) => !curr);
                  }}
                  sx={{
                    px: 0,
                    minWidth: 0,
                    mt: 0.25,
                    textTransform: "none",
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                >
                  {descriptionExpanded
                    ? "Collapse description"
                    : "Read full description"}
                </Button>
              )}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.25,
                  fontSize: "0.65rem",
                }}
              >
                Added: {dayjs(item.date).format("MM/DD/YY")}
              </Typography>
              {deadlineStr && (
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  sx={(theme) => ({
                    display: "block",
                    mt: 0.25,
                    fontSize: "0.65rem",
                    color: theme.palette.error.main,
                  })}
                >
                  Due: {deadlineStr}
                </Typography>
              )}
              {hasImages && (
                <Chip
                  size="small"
                  label="View Images"
                  variant="outlined"
                  sx={{ mt: 0.5, width: "fit-content" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    viewImagesDisclosure.onOpen();
                  }}
                />
              )}
            </Box>
          }
        />
        <Box
          sx={{
            position: "absolute",
            right: 8,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleOpen(e);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            disabled={interactionDisabled}
            sx={{
              width: 24,
              height: 24,
              "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
            }}
          >
            <MoreVert fontSize="small" />
          </IconButton>
          {hasChecklist && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen((curr) => !curr);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              disabled={interactionDisabled}
              sx={{
                width: 24,
                height: 24,
                "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
              }}
            >
              <Badge
                badgeContent={incompletedChecklistItemsCount}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    height: 12,
                    minWidth: 12,
                    fontSize: "8px",
                    fontWeight: "bold",
                  },
                  zIndex: 0,
                }}
              >
                {isOpen ? (
                  <ExpandLess fontSize="small" />
                ) : (
                  <ExpandMore fontSize="small" />
                )}
              </Badge>
            </IconButton>
          )}
        </Box>
      </ListItemButton>

      {hasChecklist && (
        <Collapse
          in={isOpen}
          timeout="auto"
          unmountOnExit
          sx={{ backgroundColor: "rgba(0,0,0,0.03)" }}
        >
          <List dense disablePadding>
            {checklist_items.map((ci, idx) => (
              <ListItem
                key={idx}
                sx={{ pl: 4 }}
                divider={idx < checklist_items.length - 1}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    disabled={interactionDisabled || completedItemActionsDisabled}
                    checked={completedChecklistItems.has(idx)}
                    onChange={(e, checked) => {
                      if (readOnly) {
                        return;
                      }
                      completedItemsChanged.current = true;
                      if (checked) {
                        setCompletedChecklistItems(
                          (curr) => new Set(curr.add(idx))
                        );
                        return;
                      }
                      setCompletedChecklistItems((curr) => {
                        const newSet = new Set(curr);
                        newSet.delete(idx);
                        return newSet;
                      });
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" component={"div"}>
                      {sanitizeHtml(ci)}
                    </Typography>
                  }
                  disableTypography
                />
              </ListItem>
            ))}
          </List>
        </Collapse>
      )}
    </Box>
  );
}
